import { useCallback, useEffect, useRef, useState } from 'react';
import { portalService } from '../../services/portal';
import type { AttachmentDownloader, AttachmentMeta, ChainEmail } from '../../types/portal';
import { formatDate, formatFileSize } from '../../utils/dateFormatters';
import { triggerBlobDownload } from '../../utils/triggerDownload';
import {
  rollupChainAttachments,
  type ChainAttachmentItem,
} from '../../utils/chainAttachmentRollup';
import { isPreviewableAttachment, type AttachmentOpenMode } from './attachmentPreview';
import styles from './ChainAttachmentRollup.module.css';

interface Props {
  emails: ChainEmail[];
  /** Override the byte fetch (Tech Support Co-Pilot uses a live-Gmail variant). */
  download?: AttachmentDownloader;
  /**
   * Beside an order, a previewable file opens inline through this callback
   * (keyed on the message that holds it) instead of downloading; the chain
   * rollup is the first PO link a CSR sees on a forwarded PO (kanban fe62ebea).
   */
  onOpenInline?: (att: AttachmentMeta, emailId: string) => void;
}

const _portalDownload: AttachmentDownloader = (att, emailId) =>
  portalService.downloadAttachment(emailId, att.filename);

/**
 * Top-of-thread attachment rollup.  Renders a single de-duplicated list
 * of every attachment in the email chain, with provenance ("from <name>
 * on <date>") preserved.  Clicking an item downloads from whichever
 * message the file actually lives on — the per-message Gmail API URL
 * is resolved via the attachment's ``emailId``.
 *
 * Renders nothing when the chain is a single message (no rollup needed —
 * the per-email ``AttachmentList`` is sufficient) or the chain has zero
 * attachments.
 */
const ChainAttachmentRollup: React.FC<Props> = ({
  emails,
  download = _portalDownload,
  onOpenInline,
}) => {
  const [loadingKey, setLoadingKey] = useState<string | null>(null);
  const blobUrlsRef = useRef<string[]>([]);

  useEffect(() => {
    return () => {
      blobUrlsRef.current.forEach((u) => URL.revokeObjectURL(u));
    };
  }, []);

  const handleDownload = useCallback(
    async (item: ChainAttachmentItem, e: React.MouseEvent) => {
      e.preventDefault();
      if (onOpenInline && isPreviewableAttachment(item.attachment.mime_type, item.attachment.filename)) {
        onOpenInline(item.attachment, item.emailId);
        return;
      }
      const key = `${item.emailId}:${item.attachment.filename}`;
      if (loadingKey) return;
      setLoadingKey(key);
      try {
        const blob = await download(item.attachment, item.emailId);
        const url = URL.createObjectURL(blob);
        blobUrlsRef.current.push(url);
        triggerBlobDownload(url, item.attachment.filename);
      } catch (err) {
        console.error('Failed to download chain attachment', err);
      } finally {
        setLoadingKey(null);
      }
    },
    [loadingKey, download, onOpenInline],
  );

  // Hide for single-message chains — per-message AttachmentList is plenty.
  if (emails.length <= 1) return null;

  const rollup = rollupChainAttachments(emails);
  if (rollup.length === 0) return null;

  return (
    <div className={styles.rollup} data-testid="chain-attachment-rollup">
      <div className={styles.rollupHeader}>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />
        </svg>
        All attachments in this chain
        <span className={styles.countBadge}>{rollup.length}</span>
      </div>
      <div className={styles.list}>
        {rollup.map((item) => {
          const key = `${item.emailId}:${item.attachment.filename}`;
          const isLoading = loadingKey === key;
          // Without an inline opener the rollup always downloads (even PDFs).
          const mode: AttachmentOpenMode =
            onOpenInline && isPreviewableAttachment(item.attachment.mime_type, item.attachment.filename)
              ? 'inline'
              : 'download';
          const dateLabel = formatDate(item.receivedAt);
          const provenance = `from ${item.fromName}${
            dateLabel !== '—' ? ` · ${dateLabel}` : ''
          }`;
          return (
            <a
              key={key}
              className={styles.item}
              href="#"
              data-action="attachment_open"
              data-target-email-id={item.emailId}
              data-outcome={mode === 'inline' ? 'open' : 'request'}
              data-record-value="mode"
              data-value={mode}
              title={item.attachment.filename}
              onClick={(e) => handleDownload(item, e)}
              style={isLoading ? { opacity: 0.6, pointerEvents: 'none' } : undefined}
            >
              <span className={styles.filename}>
                {isLoading ? 'Downloading… ' : ''}
                {item.attachment.filename}
              </span>
              <span className={styles.provenance}>{provenance}</span>
              <span className={styles.sizeMeta}>
                {formatFileSize(item.attachment.size ?? 0)}
              </span>
            </a>
          );
        })}
      </div>
    </div>
  );
};

export default ChainAttachmentRollup;
